import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, FloorLabel, Img } from "../motion/Reveal";
import { t } from "../../lib/i18n";

const FALLBACK_STEPS = [
  { title: t("Konfidensiell dialog"), text: t("Vi starter med en samtale – uten forpliktelser.") },
  { title: t("Bli kjent"), text: t("Vi lærer selskapet, menneskene og ambisjonene å kjenne.") },
  { title: t("Bygge videre"), text: t("Når vi blir eiere, begynner det langsiktige arbeidet.") },
];

/** For company owners: the question, the promise and the way forward on the left – the people on the right, framed. */
export const JoinFloor = ({ settings = {} }) => {
  const steps = Array.isArray(settings.join_steps) && settings.join_steps.length ? settings.join_steps : FALLBACK_STEPS;
  return (
    <section data-testid="floor-bli-en-del" data-ask-label={t("Spør om eierskap")} className="bg-navy text-white">
      <div className="wrap floor">
        <div className="grid-12 gap-y-12 lg:min-h-[600px]">
          <div className="col-span-12 lg:col-span-6 order-2 lg:order-1 flex flex-col justify-between lg:pr-8">
            <Reveal>
              <FloorLabel light>{t("For selskapseiere")}</FloorLabel>
              <h2 className="mt-5 text-[clamp(1.875rem,3vw,3.25rem)] font-medium leading-[1.06] tracking-[-0.03em] max-w-[20ch] text-balance">{settings.join_headline}</h2>
              <p className="mt-7 t-body text-white/75 max-w-[50ch]">{settings.join_text}</p>
              {settings.join_text_2 && <p className="mt-4 t-body text-white max-w-[50ch]">{settings.join_text_2}</p>}
              <div className="mt-10">
                <Link to="/bli-en-del-av-eqva" data-testid="career-cta-button" className="btn-pill bg-white text-navy hover:bg-surface">{t("Ta en uforpliktende prat")} <ArrowRight size={16} /></Link>
              </div>
            </Reveal>
            <Reveal delay={150} as="ol" data-testid="join-steps" className="mt-16 lg:mt-20 grid sm:grid-cols-3 border-t border-white/15">
              {steps.map((st, i) => (
                <li key={st.title} className={`pt-5 pb-4 pr-5 ${i ? "border-t border-white/15 sm:border-t-0 sm:border-l sm:pl-5" : ""}`}>
                  <span className="tabular t-label text-white/50">{String(i + 1).padStart(2, "0")}</span>
                  <span className="mt-2 block text-[15px] font-medium leading-snug">{st.title}</span>
                  {st.text && <span className="mt-1.5 block text-[14px] leading-snug text-white/65">{st.text}</span>}
                </li>
              ))}
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:col-start-8 order-1 lg:order-2">
            <div className="relative aspect-[4/3] lg:aspect-auto lg:h-full lg:min-h-[600px] overflow-hidden bg-blue">
              <Img src={settings.join_image} alt={t("Medarbeidere fra EQVA-selskapene på vei ut fra verkstedhallen")} sizes="(min-width:1024px) 42vw, 100vw" className="absolute inset-0 h-full w-full object-cover" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/** Careers get their own door – a different audience than the owners above. Photo left, words right. */
export const CareerFloor = ({ settings = {} }) => (
  <section data-testid="floor-karriere" data-ask-label={t("Spør om jobb")} className="bg-white">
    <div className="wrap floor">
      <div className="grid-12 gap-y-10 items-center">
        <Reveal soft className="col-span-12 lg:col-span-5">
          <div className="relative aspect-[4/5] overflow-hidden bg-surface img-zoom">
            <Img src={settings.career_image || "/images/karriere-laerling.jpg"} alt={t("Lærling og fagarbeider i verkstedet")} sizes="(min-width:1024px) 42vw, 100vw" className="absolute inset-0 h-full w-full object-cover" />
          </div>
        </Reveal>
        <Reveal delay={120} className="col-span-12 lg:col-span-6 lg:col-start-7">
          <FloorLabel>{t("Karriere")}</FloorLabel>
          <h2 className="mt-4 t-h2 max-w-[16ch]">{settings.career_headline || t("Bygg industrien sammen med oss")}</h2>
          <p className="mt-7 t-body text-muted max-w-[50ch]">{settings.career_text}</p>
          {settings.career_text_2 && <p className="mt-4 t-body text-ink max-w-[50ch]">{settings.career_text_2}</p>}
          <Link to="/om-oss/karriere" data-testid="career-link" className="link-arrow mt-8 text-[15px]">{t("Se mulighetene i EQVA")} <ArrowRight size={18} /></Link>
        </Reveal>
      </div>
    </div>
  </section>
);
